<template>
    <div @mouseover="hover = true"
         @mouseleave="hover = false; pressed = false"
         @mousedown="pressed = true"
         @mouseup="pressed = false; selected = !selected"
         :class="containerClass">
        <img v-if="imgSrc" :src="imgSrc" :class="imgClass">
        <ur-icon v-if="!imgSrc&&icon" :class="iconClass" icon-type="picto" >{{icon}}</ur-icon>
        <div :class="slotClass" class="line-break"><!--
            $ver: v0.1.0
            칩 내부에 위치하는 문자열 입니다.
            #A string located inside the chip.
         --><slot >{{text}}</slot>
        </div>
        <ur-icon :class="closeClass" spacing icon="close" icon-type="picto" @click="onDeleteClick"></ur-icon>
    </div>
</template>

<script>
    // ur-input-chip은 작은 정보를 표시하는 컴포넌트입니다. close 버튼을 통하여 사용자 상호작용이 가능합니다.
    // #ur-input-chip is a component that displays small information. User interaction is enabled through the close button.
    export default {
        name: "urInputChip",
        data() {
            return {
                hover: false,
                pressed: false,
                selected: false,
                default:{size: 'medium'}
            };
        },
        props: {
            // $ver: v0.2.0
            // Chip에 표시되는 문자열입니다. <em>slot</em>과 함께 쓰일 경우 <em>slot</em>이 우선순위를 갖습니다.
            // #The string value displayed on the badge. When used with <em>slot</em>, <em>slot</em> have priority.
            // $default: null
            text: {
                type: [String,Number],
                default: null,
            },
            // $ver: v0.1.0
            // 이미지 파일 경로입니다. 입력되지 않으면 칩의 이미지가 표시되지 않습니다. imgSrc 속성이 지정될 경우 icon 속성은 무시됩니다.
            // #Image file path. If not input, the image of the chip will not be displayed. If the imgSrc attribute is activated, the icon attribute is ignored.
            imgSrc: {
                type:String,
                default:'',
            },

            // $ver: v0.2.0
            // 크기 입니다. 'medium','small' 두 가지 중 하나를 입력해야 합니다.
            // #The size You must enter one of two types: medium or small.
            size:{
                type:String,
                default:'medium',
            },

            // $ver: v0.1.2
            // 기본크기 사이즈로 표현합니다.
            // #Expressed in the default size.
            // $default: false
            medium: {type:Boolean, default: false},
            // $abbr: medium
            md: {type:Boolean, default: false},
            // $ver: v0.1.2
            // 작은크기 사이즈로 표현합니다.
            // #Expressed in small size.
            // $default: false
            small: {type:Boolean, default: false},
            // $abbr: small
            sm: {type:Boolean, default: false},

            // $ver: v0.1.0
            // 색상 유형 입니다. 'primary', 'point', 'normal', 'red', 'orange', 'green' 여섯 가지 유형 중 하나를 입력해야 합니다.
            // #The color type. You must enter one of types: 'primary', 'point', 'normal', 'red', 'orange', 'green'
            color:{
                type:String,
                default:'normal',
            },

            // $ver: v0.1.0
            // 텍스트 앞에 아이콘을 표시할 경우 사용하는 속성입니다.  imgSrc 속성이 지정될 경우 icon 속성은 무시됩니다.
            // 아이콘에 대한 설명은 <a href="/#/dev/comp/icon">링크</a>를 참조하십시오.
            // #This property is used to display an icon before the text. If the imgSrc attribute is activated, the icon attribute is ignored.
            // #See the <a href="/#/dev/comp/icon">links</a> for a description of the icons.
            icon:{
                type:String,
                default:''
            },

            // $ver: v0.1.0
            // 비활성화 여부입니다. 클릭할 수 없는 상태로 바뀝니다.
            // #Whether to disable It becomes unclickable.
            // $default: false
            disabled:{
                type:Boolean,
                default:false
            },

            // $ver: v0.1.0
            // 삭제 처리 및 이벤트 발생을 위한 자기자신 참조 객체 속성입니다.
            // #Self-referenced object property for handling deletes and raising events.
            item:{
                type:Object,
            },

            // $ver: v0.1.0
            // 삭제 처리 시 실행할 함수 입니다.
            // #Function to execute when deleting.
            // hide
            delete: {
                type:Function,
            }
        },
        computed: {
            cSize() {
                if(this.size && (this.size === 'medium' || this.size === 'small')) {
                    return this.size;
                }
                if(this.medium || this.md) return 'medium';
                if(this.small || this.sm) return 'small';
                return this.default.size;
            },
            containerClass() {
                return this.disabled ? [
                    'ur-input-chip__container',
                    'ur-input-chip__container-' + this.cSize,
                    !this.imgSrc && 'ur-input-chip__container-non-image',
                    'ur-input-chip__container-disabled'
                ] : [
                    'ur-input-chip__container',
                    'ur-input-chip__container-' + this.cSize,
                    !this.imgSrc && 'ur-input-chip__container-non-image',
                    'ur-input-chip__container-' + this.color + '-input',
                ];
            },
            imgClass() {
                return [
                    'ur-input-chip__image',
                    'ur-input-chip__image-' + this.cSize
                ];
            },
            slotClass() {
                return [
                    'ur-input-chip__slot',
                    this.icon && this.cSize==='small' && 'ur-input-chip__slot-small',
                    this.disabled && 'ur-input-chip__slot-disabled'
                ];
            },
            iconDivClass() {
                return [
                    'ur-input-chip__icon-div',
                    'ur-input-chip__icon-div-' + this.cSize
                ];
            },
            iconClass() {
                return [
                    'ur-input-chip__icon' + this.cSize,
                    'ur-input-chip__icon-' + this.color
                ];
            },
            closeClass() {
                return this.disabled ? [
                    'ur-input-chip__icon',
                    'ur-input-chip__close-' + this.cSize,
                    'ur-input-chip__close-disabled'
                ] : [
                    'ur-input-chip__icon',
                    'ur-input-chip__close-' + this.cSize,
                    'ur-input-chip__close-' + this.color + '-input',
                    this.hover && 'ur-input-chip__close-' + this.color + '-input-hover',
                    this.pressed && 'ur-input-chip__close-' + this.color + '-input-active',
                ];
            },
            iconSize(){
                return this.cSize==='medium'? 8:6;
            }
        },
        methods: {
            onDeleteClick() {
                // $ver: v0.1.2
                // $arg: click event
                // Input Chip의 delete 버튼 클릭 시 발생하는 이벤트 입니다.
                // # This event occurs when the delete button of a input chip is clicked.
                this.$emit('click-delete', this.item);
            },
            mouseover(){
                this.hover = true;
            },
            mouseleave() {
                this.hover = false;
                this.pressed = false
            },
            mousedown() {
                this.pressed = true
            },
            mouseup() {
                this.pressed = false;
                this.selected = !selected
            },
        }
    }
</script>
